import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, Loader2, ArrowLeft, ArrowRight, Download, ShoppingCart, Wallet, Receipt, Percent } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { primaryText, semiBoldText, iconBgBlue, iconBgGreen, iconBgAmber, darkOrangeTextColor } from '../../../styling/stylingHelper';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import { OrderListDisplay } from './OrderListDisplay';
import { InvoiceService } from '../../../services/invoiceService';
import { StatCard } from '../../ui/StatCard';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useAppInvoiceStore } from '../../../stores/appInvoiceStore';
import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useAuth } from '../../../services/authentication/authContext';
import { OrderData } from '../../../types/invoiceTypes';
import { exportOrdersToExcel } from '../../../services/orderhistory/excelExportService';
import { format } from 'date-fns';

/**
 * Converts invoice OrderData to OrderHistory format for Excel export
 */
const convertToOrderHistory = (orders: OrderData[]): any[] => {
  return orders.map(order => ({
    id: '', // Not needed for export
    restaurantPlatformId: '',
    messageId: '',
    gmailId: '',
    platform: order.platform,
    clientName: order.clientName,
    orderNumber: order.orderNumber,
    orderDate: new Date(order.orderDate),
    total: order.total,
    totalWithTaxes: order.totalWithTaxes,
    discount: order.discount,
    shipping: order.shipping,
    productData: order.productData,
    taxCalculationMethod: order.taxCalculationMethod,
    unknownSupplierName: order.unknownSupplierName,
  }));
};


const InvoicesPage: React.FC = () => {
  const { t } = useTranslation();
  // Local state for transient UI state (resets on navigation)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Persisted state from Zustand store (survives navigation)
  const viewMode = useAppInvoiceStore(s => s.viewMode);
  const batchResult = useAppInvoiceStore(s => s.batchResult);
  const error = useAppInvoiceStore(s => s.error);
  const setViewMode = useAppInvoiceStore(s => s.setViewMode);
  const setBatchResult = useAppInvoiceStore(s => s.setBatchResult);
  const setError = useAppInvoiceStore(s => s.setError);
  const resetToUpload = useAppInvoiceStore(s => s.resetToUpload);

  // Get restaurant ID from store
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);

  // Get backend user for access control
  const { backendUser } = useAuth();
  const isLinkedDemo = !!backendUser?.demoContext;

  // Connect to order history and restaurant stores for data refresh
  const orderHistoryStore = useOrderHistory();

  const handleMultipleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isLinkedDemo) return;
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);

      if (filesArray.length > 100) {
        setError(t('invoices.errorMaxFiles'));
        return;
      }

      setSelectedFiles(filesArray);
      setError(null);
      // Note: We keep previous results in store so user can navigate back
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (isLinkedDemo) return;
    if (e.dataTransfer.files) {
      const filesArray = Array.from(e.dataTransfer.files);

      if (filesArray.length > 100) {
        setError(t('invoices.errorMaxFiles'));
        return;
      }

      setSelectedFiles(filesArray);
      setError(null);
      // Note: We keep previous results in store so user can navigate back
    }
  };

  const handleBatchExtraction = async () => {
    if (isLinkedDemo) return;
    if (selectedFiles.length === 0) {
      setError(t('invoices.errorNoFiles'));
      return;
    }

    setIsProcessing(true);
    setError(null);
    setBatchResult(null); // Clear old results when processing new files

    try {
      const data = await InvoiceService.extractBatchInvoices(
        selectedFiles,
        restaurantId
      );

      // Adjust batch result for invoice users with Hanos orders

      setBatchResult(data);
      setViewMode('results');

      // Reload order history data since new orders were created. The batch save can
      // auto-create sections/platforms server-side (e.g. a new any_invoice supplier),
      // so refresh the restaurant structure first and build the platform list from
      // the FRESH store state — the render-scope maps are stale inside this handler.
      if (restaurantId) {
        await useRestaurantStore.getState().loadAllData(true);
        const {
          restaurants: freshRestaurants,
          restaurantSections: freshSectionsMap,
          restaurantSectionPlatforms: freshPlatformMap,
          platformToSectionIndex: freshPlatformToSectionIndex,
        } = useRestaurantStore.getState();

        const restaurantIdsToReload = !isAllRestaurants
          ? [restaurantId]
          : freshRestaurants.map(r => r.id);
        const platformIds = restaurantIdsToReload.flatMap(id => {
          const sections = freshSectionsMap[id] || [];
          return sections.flatMap(s => {
            const platforms = freshPlatformMap[s.id] || [];
            return platforms.map(p => p.id);
          });
        });

        // Clear the current platform IDs to force a fresh load
        useOrderHistory.setState({ currentPlatformIds: null });
        await orderHistoryStore.loadByPlatformIds(restaurantId, platformIds, freshPlatformToSectionIndex, backendUser);
      }
    } catch (err: any) {
      setError(err.message || t('invoices.errorProcessing'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGoBackToUpload = () => {
    resetToUpload();
    // Don't clear selected files to maintain state
  };

  const handleGoToResults = () => {
    setViewMode('results');
  };

  // Handler for Excel export
  const handleExport = () => {
    if (!batchResult) return;

    // Get all orders from batch result
    const allOrders = InvoiceService.getAllOrdersFromBatch(batchResult.results);

    // Convert to OrderHistory format for export
    const orderHistoryFormat = convertToOrderHistory(allOrders);

    // Generate filename with current date
    const currentDate = format(new Date(), 'yyyy-MM-dd');
    const filename = `invoice-processing_${currentDate}`;

    // Export using the same service as order history
    exportOrdersToExcel(orderHistoryFormat, showExVatOrders, filename);
  };

  // Upload View
  if (viewMode === 'upload') {
    return (
      <>
      <MobilePageHeader title={t('nav.invoices')} />
      <div className="p-6 h-full flex flex-col">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="hidden lg:block">
            <h1 className={cn(primaryText, semiBoldText, "text-2xl mb-2")}>
              {t('invoices.title')}
            </h1>
            <p className={cn(primaryText, "text-sm opacity-70")}>
              {t('invoices.subtitle')}
            </p>
          </div>

          {/* Forward Arrow - Show when we have results to go back to */}
          {batchResult && (
            <button
              onClick={handleGoToResults}
              className="flex items-center gap-2 px-4 py-2 text-text dark:text-text-dark hover:text-accent dark:hover:text-accent-dark transition-colors"
              title={t('invoices.viewResults')}
            >
              <span className={cn(primaryText, "text-sm font-medium")}>{t('invoices.viewResults')}</span>
              <ArrowRight className="w-6 h-6" />
            </button>
          )}
        </div>

        {isLinkedDemo ? <p role="status" className="rounded-xl bg-card dark:bg-card-dark p-6 text-textPrimary dark:text-textPrimary-dark">
          {t('accountSetup.demo.invoiceImportUnavailable')}
        </p> : <div className="bg-card dark:bg-card-dark rounded-xl p-8 flex-1 flex flex-col">
          {/* File Upload */}
          <div className="mb-6 flex-1 flex flex-col">
            <label className={cn(primaryText, semiBoldText, "block mb-3 text-base")}>
              {t('invoices.uploadLabel')}
            </label>
            <label
              className="flex-1 flex flex-col items-center justify-center px-6 py-24 border-2 border-dashed border-border dark:border-border-dark rounded-lg cursor-pointer hover:border-accent dark:hover:border-accent-dark transition-all bg-background dark:bg-background-dark hover:bg-muted/5 dark:hover:bg-muted-dark/5"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <div className="text-center">
                <Upload className="mx-auto h-12 w-12 text-muted dark:text-muted-dark mb-3" />
                <span className={cn(primaryText, "text-base font-medium mb-2 block")}>
                  {selectedFiles.length > 0
                    ? t('invoices.filesSelected', { count: selectedFiles.length })
                    : t('common.fileUpload.clickOrDrag')}
                </span>
                <p className={cn(primaryText, "text-sm opacity-60")}>
                  {t('invoices.fileHint')}
                </p>
              </div>
              <input
                type="file"
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg"
                multiple
                onChange={handleMultipleFilesChange}
              />
            </label>

            {selectedFiles.length > 0 && (
              <div className="mt-4 bg-background dark:bg-background-dark rounded-lg p-4 border border-border dark:border-border-dark">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-5 w-5 text-accent dark:text-accent-dark" />
                  <span className={cn(primaryText, semiBoldText, "text-base")}>
                    {t('invoices.filesReady', { count: selectedFiles.length })}
                  </span>
                </div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {selectedFiles.map((file, idx) => (
                    <div key={idx} className={cn(primaryText, "text-sm opacity-80 pl-7")}>
                      {file.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className={cn(primaryText, "text-sm text-red-700 dark:text-red-400")}>
                {error}
              </p>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleBatchExtraction}
            disabled={selectedFiles.length === 0 || isProcessing}
            className="w-full px-6 py-3 bg-accent dark:bg-accent-dark text-white dark:text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 text-base"
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                {t('invoices.processingFiles', { count: selectedFiles.length })}
              </>
            ) : (
              <>
                <Upload className="h-5 w-5" />
                {selectedFiles.length > 0
                  ? t('invoices.processInvoices', { count: selectedFiles.length })
                  : t('invoices.processInvoicesEmpty')}
              </>
            )}
          </button>
        </div>}
      </div>
      </>
    );
  }

  // Results View
  if (viewMode === 'results' && batchResult) {
    return (
      <>
      <MobilePageHeader title={t('nav.invoices')} />
      <div className="p-6">
        {/* Header with Back Button and Export */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <ArrowLeft
              className="w-6 h-6 mr-2 cursor-pointer text-text dark:text-text-dark hover:text-accent dark:hover:text-accent-dark transition-colors"
              onClick={handleGoBackToUpload}
            />
            <div className="hidden lg:block">
              <h1 className={cn(primaryText, semiBoldText, "text-2xl mb-1")}>
                {t('invoices.resultsTitle')}
              </h1>
              <p className={cn(primaryText, "text-sm opacity-70")}>
                {t('invoices.successfullyProcessed', { count: batchResult.filesProcessed })}
              </p>
            </div>
          </div>

          {/* Export Button */}
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-accent dark:bg-accent-dark text-white dark:text-white rounded-lg font-medium hover:opacity-90 transition-all flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            {t('orderHistory.export.exportButton')}
          </button>
        </div>

        {/* Summary Stats — 2-col compact grid on phones */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4 mb-6">
          <StatCard
            icon={<ShoppingCart className="w-6 h-6" style={{ color: '#2E90FA' }} />}
            iconBg={iconBgBlue}
            title={t('invoices.totalOrders')}
            value={batchResult.summary.totalOrders}
            valueType="number"
            mobileLayout="tile"
          />
          <StatCard
            icon={<Wallet className="w-6 h-6" style={{ color: '#079455' }} />}
            iconBg={iconBgGreen}
            title={t('orderHistory.totalInclVat')}
            value={batchResult.summary.totalWithTax}
            valueType="currency"
            mobileLayout="tile"
          />
          <StatCard
            icon={<Receipt className="w-6 h-6" style={{ color: '#2E90FA' }} />}
            iconBg={iconBgBlue}
            title={t('orderHistory.totalExclVat')}
            value={batchResult.summary.totalWithoutTax}
            valueType="currency"
            mobileLayout="tile"
          />
          <StatCard
            icon={<Percent className="w-6 h-6" style={{ color: '#F59E0B' }} />}
            iconBg={iconBgAmber}
            title={t('orderHistory.totalVat')}
            value={batchResult.summary.totalVat}
            valueType="currency"
            mobileLayout="tile"
          />
        </div>

        {/* Processing Info */}
        <div className="bg-card dark:bg-card-dark rounded-xl p-4 mb-6">
          <div className="flex flex-wrap gap-4 text-sm">
            <div>
              <span className={cn(primaryText, "opacity-60")}>{t('invoices.filesProcessed')}: </span>
              <span className={cn(primaryText, semiBoldText)}>{batchResult.filesProcessed}</span>
            </div>
            <div>
              <span className={cn(primaryText, "opacity-60")}>{t('invoices.duration')}: </span>
              <span className={cn(primaryText, semiBoldText)}>{batchResult.duration.toFixed(2)}s</span>
            </div>
            {/* Orders the backend deduped against an existing one. Silent until now — the invoice
                looked processed while nothing was stored. */}
            {(batchResult.saveStats?.skippedDuplicates ?? 0) > 0 && (
              <div>
                <span className={cn(primaryText, "opacity-60")}>
                  {t('invoices.skippedAsDuplicates', { defaultValue: 'Skipped as duplicates' })}:{' '}
                </span>
                <span className={cn(semiBoldText, darkOrangeTextColor)}>
                  {batchResult.saveStats?.skippedDuplicates}
                </span>
              </div>
            )}
            {/* <div>
              <span className={cn(primaryText, "opacity-60")}>API Costs: </span>
              <span className={cn(primaryText, semiBoldText)}>€{(batchResult.summary.totalCosts * 100).toFixed(2)} cents</span>
            </div> */}
          </div>
        </div>

        {/* Orders List */}
        <div className="bg-card dark:bg-card-dark rounded-xl p-6">
          <OrderListDisplay
            orders={InvoiceService.getAllOrdersFromBatch(batchResult.results)}
            fileResults={batchResult.results}
            savedOrders={batchResult.saveStats?.savedOrders}
          />
        </div>
      </div>
      </>
    );
  }

  return null;
};

export default InvoicesPage;
